import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2, ImagePlus, Star } from "lucide-react";
import { api, resolveImage } from "../lib/api";
import { toast } from "sonner";

/**
 * ImageUploader
 *   - up to `max` images (default 20)
 *   - shows preview + remove
 *   - loud error toasts on failure (never silent)
 *
 * Note: we intentionally DO NOT set the Content-Type header — axios detects the
 * FormData body and sets the correct `multipart/form-data; boundary=...`.
 */
export default function ImageUploader({ images, onChange, max = 20 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (images.length + list.length > max) {
      toast.error(`You can upload up to ${max} images. ${images.length} already added.`);
      return;
    }

    setUploading(true);
    const uploaded = [];
    let failed = 0;

    for (const file of list) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10 MB — skipped.`);
        failed += 1;
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd);
        if (data?.url) uploaded.push(data.url);
        else {
          failed += 1;
          toast.error(`Upload failed for ${file.name}: server returned no URL`);
        }
      } catch (err) {
        failed += 1;
        const detail = err?.response?.data?.detail;
        const reason =
          typeof detail === "string"
            ? detail
            : err?.response?.status === 401
            ? "Please login again to upload images."
            : `Failed to upload ${file.name}`;
        toast.error(reason);
      }
    }

    if (uploaded.length) {
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded${failed ? ` · ${failed} failed` : ""}`);
    }
    setUploading(false);
    // reset input so the same file can be re-selected
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));
  const setCover = (idx) => {
    if (idx === 0 || idx < 0 || idx >= images.length) return;
    const next = images.slice();
    const [cover] = next.splice(idx, 1);
    next.unshift(cover);
    onChange(next);
  };

  return (
    <div data-testid="image-uploader">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
        data-testid="upload-dropzone"
      >
        {uploading ? <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" /> : <UploadCloud className="h-8 w-8 mx-auto text-primary" />}
        <p className="mt-2 text-sm font-medium text-ink">Drag & drop or tap to upload</p>
        <p className="text-xs text-muted-foreground">Up to {max} images · max 10 MB each · JPG, PNG, WebP</p>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="upload-input" />
      </div>

      {images.length > 0 && (
        <>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
            {images.map((src, i) => (
              <div key={i} className={`relative group rounded-xl overflow-hidden border shadow-sm transition-all ${i === 0 ? "border-accent ring-2 ring-accent/40" : "border-border"}`}>
                <img src={resolveImage(src)} alt={`Upload ${i + 1}`} className="w-full aspect-square object-cover" loading="lazy" />
                <button type="button" onClick={() => remove(i)} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-destructive transition-colors" data-testid={`remove-image-${i}`}><X className="h-3.5 w-3.5" /></button>
                {i === 0 ? (
                  <span className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold uppercase tracking-wide bg-accent text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    <Star className="h-2.5 w-2.5 fill-current" /> Cover
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCover(i)}
                    className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold uppercase tracking-wide bg-black/70 hover:bg-accent text-white px-1.5 py-0.5 rounded transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`set-cover-${i}`}
                    title="Set as cover"
                  >
                    Set cover
                  </button>
                )}
              </div>
            ))}
            {images.length < max && (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                data-testid="add-more-images"
              >
                <ImagePlus className="h-6 w-6" />
                <span className="text-[11px] font-medium">Add more</span>
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{images.length}/{max} images · Hover any image and click <strong>Set cover</strong> to make it the main photo.</p>
        </>
      )}
    </div>
  );
}
